'use client'

import { Accordion, Checkbox, NumberInput, Select, Switch, TextInput, Textarea } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates";
import { toast } from "sonner"
import { ICycle } from "@/models/types/cycle"
import { PlantFormType } from "../plantFields"
import { INote } from "@/models/types/note"
import { UploadButton } from "@/utils/uploadthing";
import Image from "next/image";

const toDateValue = (value: Date | string | null | undefined) => {
    if (!value) {
        return null;
    }

    return value instanceof Date ? value : new Date(value);
}

export const PlantName = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <TextInput
            id="plant-name"
            name="plant-name"
            label="Plant Name"
            placeholder="Enter Plant Name"
            radius={"md"}
            key={plantForm.key('plant.name')}
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            required
            {...plantForm.getInputProps('plant.name')} />

    )
}

export const PlantDescription = ({ plantForm }: { plantForm: PlantFormType }) => (
    <Textarea
        id="plant-description"
        name="plant-description"
        label="Notes"
        placeholder="Growth habits, care quirks, or anything worth remembering"
        radius="md"
        autosize
        minRows={3}
        key={plantForm.key('plant.description')}
        {...plantForm.getInputProps('plant.description')}
    />
)

export const PlantSeason = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="plant-season"
            name="plant-season"
            label="Best Planting Season"
            placeholder="Select Best Planting Season"
            radius={"md"}
            data={[
                { value: 'Spring', label: 'Spring', disabled: plantForm.getValues().plant.seasonToAvoid === 'Spring' },
                { value: 'Summer', label: 'Summer', disabled: plantForm.getValues().plant.seasonToAvoid === 'Summer' },
                { value: 'Autumn', label: 'Autumn', disabled: plantForm.getValues().plant.seasonToAvoid === 'Autumn' },
                { value: 'Winter', label: 'Winter', disabled: plantForm.getValues().plant.seasonToAvoid === 'Winter' },
            ]}
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            key={plantForm.key('plant.season')}
            {...plantForm.getInputProps('plant.season')}
        />
    )
}

export const PlantSeasonToAvoid = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="plant-season-to-avoid"
            name="plant-season-to-avoid"
            label="Plant Season to Avoid"
            placeholder="Select Plant Season to Avoid"
            radius={"md"}
            data={[
                { value: 'Spring', label: 'Spring', disabled: plantForm.getValues().plant.season === 'Spring' },
                { value: 'Summer', label: 'Summer', disabled: plantForm.getValues().plant.season === 'Summer' },
                { value: 'Autumn', label: 'Autumn', disabled: plantForm.getValues().plant.season === 'Autumn' },
                { value: 'Winter', label: 'Winter', disabled: plantForm.getValues().plant.season === 'Winter' },
            ]}
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            key={plantForm.key('plant.seasonToAvoid')}
            {...plantForm.getInputProps('plant.seasonToAvoid')}
        />
    )
}

// Time to Grow
export const PlantTimeToGrow = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <div className="flex flex-col justify-around-items-center w-full">
            <p className="text-[14px] font-semibold w-fit">{`Approx. Time to Grow (Min/Max)`}</p>
            <span className="col-span-1 flex flex-row justify-around items-center w-full">
                <NumberInput
                    id="time-to-grow-min"
                    name="time-to-grow-min"
                    placeholder="Select Aprrox. Min Time to Grow"
                    radius="md"
                    styles={{
                        input: { height: heightToUse, fontSize: '14px' },
                    }}
                    min={0}
                    step={1}
                    type="text"
                    key={plantForm.key('plant.timeToGrow.min')}
                    {...plantForm.getInputProps('plant.timeToGrow.min')}
                />
                <NumberInput
                    id="time-to-grow-max"
                    name="time-to-grow-max"
                    placeholder="Select Aprrox. Max Time to Grow"
                    radius="md"
                    styles={{
                        input: { height: heightToUse, fontSize: '14px' },
                    }}
                    min={0}
                    step={1}
                    type="text"
                    key={plantForm.key('plant.timeToGrow.max')}
                    {...plantForm.getInputProps('plant.timeToGrow.max')}
                />
            </span>
        </div>
    )
};

// Water Frequency
export const PlantWaterFrequency = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {
    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="water-frequency"
            name="water-frequency"
            label="Water Frequency"
            placeholder="Select Water Frequency"
            radius="md"
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            data={[
                { value: 'Daily', label: 'Daily' },
                { value: 'Weekly', label: 'Weekly' },
                { value: 'Biweekly', label: 'Biweekly' },
                { value: 'Monthly', label: 'Monthly' },
            ]}
            key={plantForm.key('plant.waterFrequency')}
            {...plantForm.getInputProps('plant.waterFrequency')}
        />
    )
};

// Soil Type
export const PlantSoilType = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <TextInput
            id="soil-type"
            name="soil-type"
            label="Soil Type"
            placeholder="Select Soil Type"
            radius="md"
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            key={plantForm.key('plant.soilType')}
            {...plantForm.getInputProps('plant.soilType')}
        />
    )
}

export const PlantCareSchedule = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {
    const heightToUse = heightQuery / 15;
    const lastWateredAt = toDateValue(plantForm.getValues().plant.careSchedule?.lastWateredAt);
    const nextWateringAt = toDateValue(plantForm.getValues().plant.careSchedule?.nextWateringAt);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <NumberInput
                id="watering-interval"
                name="watering-interval"
                label="Water every"
                placeholder="Days"
                suffix=" days"
                radius="md"
                min={0}
                step={1}
                styles={{
                    input: { height: heightToUse, fontSize: '14px' },
                }}
                key={plantForm.key('plant.careSchedule.wateringIntervalDays')}
                {...plantForm.getInputProps('plant.careSchedule.wateringIntervalDays')}
            />
            <DatePickerInput
                id="last-watered"
                label="Last watered"
                placeholder="Pick date"
                radius="md"
                value={lastWateredAt}
                onChange={(value) => plantForm.setFieldValue('plant.careSchedule.lastWateredAt', value)}
                styles={{
                    input: { height: heightToUse, fontSize: '14px' },
                }}
            />
            <DatePickerInput
                id="next-watering"
                label="Next watering"
                placeholder="Pick date"
                radius="md"
                value={nextWateringAt}
                onChange={(value) => plantForm.setFieldValue('plant.careSchedule.nextWateringAt', value)}
                styles={{
                    input: { height: heightToUse, fontSize: '14px' },
                }}
            />
            <div className="flex h-full min-h-16 items-end">
                <Switch
                    id="watering-reminders"
                    label="Reminders"
                    key={plantForm.key('plant.careSchedule.reminderEnabled')}
                    {...plantForm.getInputProps('plant.careSchedule.reminderEnabled', { type: 'checkbox' })}
                />
            </div>
        </div>
    )
};

export const PlantPhotoUpload = ({ plantForm }: { plantForm: PlantFormType }) => {
    const imageUrl = plantForm.getValues().plant.imageUrl;

    return (
        <div className="flex flex-col gap-3 rounded-md border border-emerald-200 bg-white p-3">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-md bg-emerald-950/10">
                {imageUrl ? (
                    <Image
                        src={imageUrl}
                        alt={`${plantForm.getValues().plant.name || 'Plant'} photo`}
                        width={640}
                        height={480}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm font-medium text-emerald-900/60">
                        Add a plant photo
                    </div>
                )}
            </div>
            <UploadButton
                endpoint="plantImage"
                onClientUploadComplete={(res) => {
                    const uploadedFile = res?.[0] as { ufsUrl?: string; url?: string } | undefined;
                    const nextUrl = uploadedFile?.ufsUrl ?? uploadedFile?.url ?? null;

                    if (!nextUrl) {
                        toast.error("Upload finished, but no image URL came back.");
                        return;
                    }

                    plantForm.setFieldValue('plant.imageUrl', nextUrl);
                    toast.success("Plant photo uploaded.");
                }}
                onUploadError={(error: Error) => {
                    toast.error(error.message);
                }}
            />
            <TextInput
                id="plant-image-url"
                name="plant-image-url"
                label="Image URL"
                placeholder="Paste an image URL"
                radius="md"
                key={plantForm.key('plant.imageUrl')}
                {...plantForm.getInputProps('plant.imageUrl')}
            />
        </div>
    )
}

// Fertilizer Type
export const PlantFertilizerType = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="fertilizer-type"
            name="fertilizer-type"
            label="Fertilizer Type"
            placeholder="Select Fertilizer Type"
            radius="md"
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            data={[
                { value: 'Organic', label: 'Organic' },
                { value: 'Inorganic', label: 'Inorganic' },
                { value: 'Slow-release', label: 'Slow-release' },
                { value: 'Liquid', label: 'Liquid' },
            ]}
            key={plantForm.key('plant.fertitizer.type')}
            {...plantForm.getInputProps('plant.fertilizer.type')}
        />
    )
};

export const PlantNPKRatio = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {
    const heightToUse = heightQuery / 15;
    return (
        <span className="col-span-1 flex flex-row justify-around items-center ">
            <NumberInput
                id="fertilizer-nitrogen"
                name="fertilizer-nitrogen"
                label="N%"
                placeholder="Enter Nitrogen Value"
                radius="md"
                styles={{
                    input: { height: heightToUse, fontSize: '14px' },
                }}
                min={0}
                step={1}
                key={plantForm.key('plant.fertilizer.npkRatio.nitrogen')}
                {...plantForm.getInputProps('plant.fertilizer.npkRatio.nitrogen')}
            />
            <NumberInput
                id="fertilizer-phosphorus"
                name="fertilizer-phosphorus"
                label="P%"
                placeholder="Enter Phosphorus Value"
                radius="md"
                styles={{
                    input: { height: heightToUse, fontSize: '14px' },
                }}
                min={0}
                step={1}
                key={plantForm.key('plant.fertilizer.npkRatio.phosphorus')}
                {...plantForm.getInputProps('plant.fertilizer.npkRatio.phosphorus')}
            />
            <NumberInput
                id="fertilizer-potassium"
                name="fertilizer-potassium"
                label="K%"
                placeholder="Enter Potassium Value"
                radius="md"
                styles={{
                    input: { height: heightToUse, fontSize: '14px' },
                }}
                min={0}
                step={1}
                key={plantForm.key('plant.fertilizer.npkRatio.potassium')}
                {...plantForm.getInputProps('plant.fertilizer.npkRatio.potassium')}
            />
        </span>
    )
}

// Sunlight Requirement
export const PlantSunlightRequirement = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="sunlight-intensity"
            name="sunlight-intensity"
            label="Sunlight Intensity"
            placeholder="Select Sunlight Intensity"
            radius="md"
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            data={[
                { value: 'Full Sun', label: 'Full Sun' },
                { value: 'Partial Shade', label: 'Partial Shade' },
                { value: 'Full Shade', label: 'Full Shade' },
            ]}
            key={plantForm.key('plant.sunlight.requirement')}
            {...plantForm.getInputProps('plant.sunlight.requirement')}
        />
    )
};

export const PlantSunlightHours = ({ plantForm, heightQuery }: { plantForm: PlantFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;
    //const inital = plantForm.getInitialValues().plant.sunlight.hoursOfSunlight;

    const data = Array.from({ length: 24 }, (_, i) => ({
        value: `${i + 1}`,
        label: `${i + 1} Hour${i === 0 ? '' : 's'}`
    }));

    return (
        <Select
            id="sunlight-hours"
            name="sunlight-hours"
            label="Hours of Sunlight"
            placeholder="Select Hours of Sunlight"
            radius="md"
            styles={{
                input: { height: heightToUse, fontSize: '14px' },
            }}
            data={data}
            key={plantForm.key('plant.sunlight.hoursOfSunlight')}
            {...plantForm.getInputProps('plant.sunlight.hoursOfSunlight')}
        />
    )
}

// Indoor/Outdoor Checkbox Group
export default function PlantIndoorOutdoor({ plantForm }: { plantForm: PlantFormType }) {

    return (
        <div className="w-full flex flex-row justify-evenly items-center bg-white rounded-md p-2">
            <Checkbox
                styles={{ input: { cursor: 'pointer' } }}
                id="isIndoor"
                name="isIndoor"
                label="Indoor"
                key={plantForm.key('plant.isIndoor')}
                {...plantForm.getInputProps('plant.isIndoor', { type: 'checkbox' })}
            />
            <Checkbox
                styles={{ input: { cursor: 'pointer' } }}
                id="isOutdoor"
                name="isOutdoor"
                label="Outdoor"
                key={plantForm.key('plant.isOutdoor')}
                {...plantForm.getInputProps('plant.isOutdoor', { type: 'checkbox' })}
            />
        </div>
    )
}


export const GrowingPlants = ({ growing, handleCloseDrops, cycleOpen, basicRowCss }: { growing: ICycle[], handleCloseDrops: (which: "notes" | "growing") => void, cycleOpen: boolean, basicRowCss: string }) => {

    return (
        growing && growing.length > 0 ? (
            <Accordion value={cycleOpen ? 'open' : 'close'} transitionDuration={200} className="w-full" radius={'md'}>
                <Accordion.Item value="open">
                    <Accordion.Control onClick={() => handleCloseDrops('growing')} className="cursor-pointer rounded-md">
                        {cycleOpen ? 'Hide Growing' : 'Show Growing'}
                    </Accordion.Control>
                    <Accordion.Panel mt={'sm'}>
                        {growing.length > 0 ? (
                            <ol className="list-decimal list-inside space-y-2">
                                {growing.map((cycle, index) => {
                                    return (
                                        <li className={basicRowCss} key={index}>
                                            <button onClick={() => toast.info("This would take you to this round's info")}>{cycle._id}</button>
                                        </li>
                                    )
                                })}
                            </ol>
                        ) : (
                            <p className="text-gray-500">No growing recorded yet.</p>
                        )}
                        <button
                            className="mt-2 text-blue-500 hover:underline cursor-pointer"
                            onClick={() => toast.info('This would open a modal to add a new plant to grow')}
                        >
                            Begin Tracking cycle
                        </button>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>
        ) : (
            <button
                className="mt-2 text-blue-500 hover:underline cursor-pointer"
                onClick={() => toast.info('This would open a modal to add a new round')}
            >
                Add Round
            </button>
        ))
}


export const PlantNotes = ({ plantNotes, notes, handleCloseDrops, basicRowCss }: { plantNotes: INote[], notes: boolean, handleCloseDrops: (which: "notes" | "growing") => void, basicRowCss: string }) => {
    return (
        plantNotes && plantNotes.length > 0 ? (
            <Accordion value={notes ? 'open' : 'close'} transitionDuration={200} className="w-full" radius={'md'}>
                <Accordion.Item value="open">
                    <Accordion.Control onClick={() => handleCloseDrops('notes')} className="cursor-pointer rounded-md" >
                        {notes ? 'Hide Notes' : 'Show Notes'}
                    </Accordion.Control>
                    <Accordion.Panel mt={'sm'}>
                        <ol className="list-decimal list-inside space-y-2">
                            {plantNotes.map((note, index) => {
                                return (
                                    <li className={basicRowCss} key={index}>{note.note}</li>
                                )
                            })}
                        </ol>
                        <button
                            className="mt-2 text-blue-500 hover:underline cursor-pointer"
                            onClick={() => toast.info('This would open a modal to add a new note')}
                        >
                            Add Note
                        </button>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>
        ) : (
            <button
                className="mt-2 text-blue-500 hover:underline cursor-pointer"
                onClick={() => toast.info('This would open a modal to add a new note')}
            >
                Add Note
            </button>
        ))
}
